import React from 'react'
import classnames from 'classnames'
import { varianceColorClass } from '../cellColor'
import NullDash from './NullDash'

export type SignedMoneyCellRole = 'variance'

export interface SignedMoneyCellProps {
  value: number | null | undefined
  /** 0 for whole dollars; 2 for cents. Default 2. */
  fractionDigits?: 0 | 2
  className?: string
  /** Semantic coloring; overridden by explicit valueClassName. */
  role?: SignedMoneyCellRole
  /** Optional class for the numeric portion (the $ stays muted). */
  valueClassName?: string
}

/** Table-friendly signed currency: +/- prefix, muted $, tabular amount. Zero omits sign. */
export const SignedMoneyCell: React.FC<SignedMoneyCellProps> = ({
  value,
  fractionDigits = 2,
  className,
  role,
  valueClassName
}) => {
  if (value == null || Number.isNaN(value)) {
    return <NullDash className={className} />
  }
  const absFormatted =
    fractionDigits === 0
      ? Math.round(Math.abs(value)).toLocaleString()
      : Math.abs(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
  const nearZero = Math.abs(value) < 0.005
  const sign = nearZero ? '' : value < 0 ? '-' : '+'
  const numText = `${sign}${absFormatted}`
  const colorClass = classnames(
    role === 'variance' ? varianceColorClass(value) : undefined,
    valueClassName
  )

  return (
    <span className={classnames('inline-flex items-center whitespace-nowrap tabular-nums', className)}>
      <span className="text-xs font-normal text-slate-400 dark:text-slate-400">$</span>
      <span className={classnames('ml-1', colorClass)}>{numText}</span>
    </span>
  )
}

export default SignedMoneyCell
